import * as Icons from 'lucide-react'
import { MetricCard } from './MetricCard'
import { ComboChart, ComboChartData } from './ComboChart'
import { HourlyTrendChart, HourlyDataPoint } from './HourlyTrendChart'

export interface OverallMetrics {
  blendedRoas: number
  targetBlendedRoas?: number
  spend: number
  targetSpend?: number
  grossRevenue: number
  targetGrossRevenue?: number
  grossProfit: number
  targetGrossProfit?: number
  cvr: number
  targetCvr?: number
}

export interface DailyData {
  date: string
  spend: number
  revenue: number
  blendedRoas: number
  paidMediaRoas?: number
  grossProfit: number
  cvr: number
}

export interface HourlyMetrics {
  spend: number
  revenue: number
  blended_roas: number
  gross_profit: number
  cvr: number
  impressions: number
  clicks: number
  conversions: number
}

export interface HourlySummaryData {
  hour: number
  yesterday_total: HourlyMetrics
  yesterday: HourlyMetrics
  today: HourlyMetrics
}

export interface HourlyTotals {
  yesterday_total: HourlyMetrics
  yesterday: HourlyMetrics
  today: HourlyMetrics
}

interface OverallPerformanceSectionProps {
  metrics: OverallMetrics | null
  dailyData: DailyData[]
  hourlyData?: HourlySummaryData[]
  hourlyTotals?: HourlyTotals
  currentHour?: number
  loading?: boolean
  onRefresh?: () => void
  cogsPercentage?: number // 0.0 to 1.0 (e.g., 0.45 = 45%)
}

// Helper to transform hourly API data to chart format for a specific metric
function transformHourlyData(
  hourlyData: HourlySummaryData[],
  metric: 'spend' | 'revenue' | 'blended_roas' | 'gross_profit' | 'cvr'
): HourlyDataPoint[] {
  return hourlyData.map(h => ({
    hour: h.hour,
    yesterdayTotal: h.yesterday_total?.[metric] || 0,
    yesterday: h.yesterday?.[metric] || 0,
    today: h.today?.[metric] || 0,
  }))
}

export function OverallPerformanceSection({
  metrics,
  dailyData,
  hourlyData,
  hourlyTotals,
  currentHour = new Date().getHours(),
  loading,
  onRefresh,
  cogsPercentage = 0,
}: OverallPerformanceSectionProps) {
  // Convert daily data to combo chart format
  const comboData: ComboChartData[] = dailyData.map(d => ({
    date: d.date,
    spend: d.spend,
    blendedRoas: d.blendedRoas,
    paidMediaRoas: d.paidMediaRoas,
  }))

  // Check if we have hourly data
  const hasHourlyData = hourlyData && hourlyData.length > 0 && hourlyTotals

  // Transform hourly data for each metric
  const spendHourly = hasHourlyData ? transformHourlyData(hourlyData, 'spend') : []
  const roasHourly = hasHourlyData ? transformHourlyData(hourlyData, 'blended_roas') : []
  const revenueHourly = hasHourlyData ? transformHourlyData(hourlyData, 'revenue') : []
  const profitHourly = hasHourlyData ? transformHourlyData(hourlyData, 'gross_profit') : []
  const cvrHourly = hasHourlyData ? transformHourlyData(hourlyData, 'cvr') : []

  // Helper to get totals for a specific metric
  const getTotals = (metric: 'spend' | 'revenue' | 'blended_roas' | 'gross_profit' | 'cvr') => {
    if (!hourlyTotals) return { yesterdayTotal: 0, yesterday: 0, today: 0 }
    return {
      yesterdayTotal: hourlyTotals.yesterday_total?.[metric] || 0,
      yesterday: hourlyTotals.yesterday?.[metric] || 0,
      today: hourlyTotals.today?.[metric] || 0,
    }
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <Icons.BarChart3 className="w-5 h-5 text-tf-text-muted" />
        <h2 className="text-lg font-semibold text-tf-text">Overall Performance</h2>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <MetricCard
          title="Blended ROAS"
          value={metrics?.blendedRoas || 0}
          previousValue={metrics?.targetBlendedRoas}
          loading={loading}
        />
        <MetricCard
          title="Spend"
          value={metrics?.spend || 0}
          previousValue={metrics?.targetSpend}
          prefix="$"
          loading={loading}
        />
        <MetricCard
          title="Gross Revenue"
          value={metrics?.grossRevenue || 0}
          previousValue={metrics?.targetGrossRevenue}
          prefix="$"
          loading={loading}
        />
        <MetricCard
          title="Gross Profit"
          subtitle={cogsPercentage > 0 ? `(${Math.round(cogsPercentage * 100)}% COGS applied)` : undefined}
          value={metrics?.grossProfit || 0}
          previousValue={metrics?.targetGrossProfit}
          prefix="$"
          loading={loading}
        />
        <MetricCard
          title="Conversion Rate"
          value={metrics?.cvr || 0}
          previousValue={metrics?.targetCvr}
          suffix="%"
          loading={loading}
        />
      </div>

      {/* Charts Grid - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <ComboChart
          title="Blended ROAS & Spend"
          data={comboData}
          loading={loading}
        />
        {hasHourlyData ? (
          <>
            <HourlyTrendChart
              title="Spend"
              data={spendHourly}
              totals={getTotals('spend')}
              currentHour={currentHour}
              loading={loading}
              isCurrency
              onRefresh={onRefresh}
            />
            <HourlyTrendChart
              title="Blended ROAS"
              data={roasHourly}
              totals={getTotals('blended_roas')}
              currentHour={currentHour}
              loading={loading}
              onRefresh={onRefresh}
            />
          </>
        ) : (
          <>
            <div className="bg-tf-bg-muted rounded-xl border border-tf-border p-4 flex items-center justify-center h-64">
              <p className="text-tf-text-muted text-sm">Hourly data loading...</p>
            </div>
            <div className="bg-tf-bg-muted rounded-xl border border-tf-border p-4 flex items-center justify-center h-64">
              <p className="text-tf-text-muted text-sm">Hourly data loading...</p>
            </div>
          </>
        )}
      </div>

      {/* Charts Grid - Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {hasHourlyData ? (
          <>
            <HourlyTrendChart
              title="Gross Revenue"
              data={revenueHourly}
              totals={getTotals('revenue')}
              currentHour={currentHour}
              loading={loading}
              isCurrency
              onRefresh={onRefresh}
            />
            <HourlyTrendChart
              title="Gross Profit"
              data={profitHourly}
              totals={getTotals('gross_profit')}
              currentHour={currentHour}
              loading={loading}
              isCurrency
              onRefresh={onRefresh}
            />
            <HourlyTrendChart
              title="CVR"
              data={cvrHourly}
              totals={getTotals('cvr')}
              currentHour={currentHour}
              loading={loading}
              isPercentage
              onRefresh={onRefresh}
            />
          </>
        ) : (
          <>
            <div className="bg-tf-bg-muted rounded-xl border border-tf-border p-4 flex items-center justify-center h-64">
              <p className="text-tf-text-muted text-sm">Hourly data loading...</p>
            </div>
            <div className="bg-tf-bg-muted rounded-xl border border-tf-border p-4 flex items-center justify-center h-64">
              <p className="text-tf-text-muted text-sm">Hourly data loading...</p>
            </div>
            <div className="bg-tf-bg-muted rounded-xl border border-tf-border p-4 flex items-center justify-center h-64">
              <p className="text-tf-text-muted text-sm">Hourly data loading...</p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default OverallPerformanceSection
